import { Link } from 'react-router-dom';
import { DashboardTodayCard } from './DashboardTodayCard';
import { Blocks, CheckCircle2, Lock, RefreshCw, UserRound, Users, Wallet, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchOrgClientsList } from '../../features/clients/api/clients.api';
import type { OrgClientSummary } from '../../features/clients/api/clients.api';
import {
  fetchPaymentGatewayAuditListAdmin,
  fetchPaymentGatewayAuditStats,
  fetchPaymentGatewayAuditTotalsAdmin
} from '../../features/payment-gateway-audit/api/payment-gateway-audit.api';
import type {
  PaymentGatewayAuditDashboardStats,
  PaymentGatewayAuditEntry,
  PaymentGatewayAuditTotals
} from '../../features/payment-gateway-audit/api/payment-gateway-audit.types';
import { fetchOrgPaymentsStats } from '../../features/payments/api/payments.api';
import type { PaymentStatsDto } from '../../features/superadmin/api/payments-stats.api';
import { fetchOperationalPaymentsList } from '../../features/payments/api/payments.api';
import type { OperationalPaymentRow } from '../../features/payments/api/payments.types';
import {
  formatPaymentAmount,
  paymentProviderLabel,
  paymentStatusLabel
} from '../../features/payments/lib/payment-display';
import { fetchOrgPlanStatus } from '../../features/tariffs/api/org-tariff.api';
import {
  buildLifecycleSummaryLine,
  buildLimitsSummaryText,
  buildNormalSubscriptionStatusLine,
  moduleCodeLabelRu
} from '../../features/tariffs/lib/org-tariff-page-view-model';
import type { OrgPlanStatus } from '../../features/tariffs/lib/tariff-org-types';
import {
  getOrganizationMeModules,
  type OrgModuleItem
} from '../../features/org-settings/api/org-settings.api';
import { fetchAdminTrainersList } from '../../features/trainers/api/trainers.api';
import { fetchYcAdminSyncStatus } from '../../features/yclients-sync/api/yclients-sync.api';
import type { YcAdminSyncStatus } from '../../features/yclients-sync/api/yclients-sync.types';
import { fetchTenantBranches } from '../../features/organization-branches/api/organization-branches.api';
import { hasPermission, isModuleEnabled, isRouteAllowed } from '../../shared/access/access';
import { useAuth } from '../../shared/auth/use-auth';
import { formatOrgDate } from '../../shared/lib/format-org-datetime';
import { useOrgTimezone } from '../../shared/lib/use-org-timezone';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Alert } from '../../shared/ui/Alert';
import { Badge } from '../../shared/ui/Badge';
import { Button, getButtonClassNames } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { EmptyState } from '../../shared/ui/EmptyState';
import { Spinner } from '../../shared/ui/Spinner';
import { Stack } from '../../shared/ui/Stack';
import { Table, TableScroll, TBody, Td, Th, THead, Tr } from '../../shared/ui/Table';
import { staffRoleLabel } from '../../widgets/topbar/staff-role-label';

function last30dRange(): { created_from: string; created_to: string } {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 30);
  return { created_from: start.toISOString(), created_to: end.toISOString() };
}

function formatRubMoney(amount: number): string {
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ₽`;
  }
}

function filterByBranch<T extends { branch_id?: number | null }>(rows: T[], branchId: number | null): T[] {
  if (branchId == null) return rows;
  return rows.filter((r) => r.branch_id === branchId);
}

type DashboardSection = 'plan' | 'clients' | 'trainers' | 'yc' | 'auditTotals' | 'payments' | 'paymentRows' | 'modules';

type DashboardSectionErrors = Record<DashboardSection, string | null>;

const EMPTY_SECTION_ERRORS: DashboardSectionErrors = {
  plan: null,
  clients: null,
  trainers: null,
  yc: null,
  auditTotals: null,
  payments: null,
  paymentRows: null,
  modules: null
};

function RetryButton({ onRetry, disabled, className }: { onRetry: () => void; disabled?: boolean; className?: string }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onRetry}
      disabled={disabled}
      className={className ?? 'inline-flex items-center gap-1.5'}
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden />
      Повторить
    </Button>
  );
}

function RefreshWarning({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <Alert variant="warning" title="Не удалось обновить данные">
      <p className="staff-m-0 text-sm">{error}</p>
      <p className="staff-m-0 mt-1 text-xs">Показаны ранее загруженные данные.</p>
      <RetryButton onRetry={onRetry} className="mt-2 inline-flex items-center gap-1.5" />
    </Alert>
  );
}

/** Сводка org (паритет `OrgOperationalDashboard` в donor CRM); показ по `isOrgTenantDashboardOpsAudience` (контекст организации в `/auth/me`). */
export default function TenantOrgOperationalDashboard() {
  const { apiClient, me } = useAuth();
  const caps = me?.capabilities ?? null;
  const branchScopeId = me?.active_branch_id ?? null;
  const organizationId = me?.organization_id != null && me.organization_id > 0 ? me.organization_id : null;
  const tz = useOrgTimezone({ apiClient, organizationId: me?.organization_id ?? null, branchId: branchScopeId });

  const canClients = isRouteAllowed(caps, '/clients');
  /** Каталог staff: `/staff` привязан к модулю `fitness` — без модуля счётчик не показываем. */
  const canTrainers = isRouteAllowed(caps, '/staff') && isModuleEnabled(caps, 'fitness');
  const canYc =
    isModuleEnabled(caps, 'yclients') &&
    hasPermission(caps, 'yclients:read') &&
    isRouteAllowed(caps, '/yc-sync-summary');
  /** Аудит шлюза — контур платежей; без модуля `payments` в тарифе блок не показываем. */
  const canAuditPay = isModuleEnabled(caps, 'payments') && hasPermission(caps, 'audit:org_read');
  const canTariffs = isRouteAllowed(caps, '/tariffs');

  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [branchName, setBranchName] = useState<string | null>(null);
  const [planStatus, setPlanStatus] = useState<OrgPlanStatus | null>(null);
  const [clientsTotal, setClientsTotal] = useState<number | null>(null);
  const [clientsRows, setClientsRows] = useState<OrgClientSummary[]>([]);
  const [trainersTotal, setTrainersTotal] = useState<number | null>(null);
  const [yc, setYc] = useState<YcAdminSyncStatus | null>(null);
  const [payTotals, setPayTotals] = useState<PaymentGatewayAuditTotals | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_payStats, setPayStats] = useState<PaymentGatewayAuditDashboardStats | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_paymentRows, setPaymentRows] = useState<PaymentGatewayAuditEntry[]>([]);
  const [orgPayStats, setOrgPayStats] = useState<PaymentStatsDto | null>(null);
  const [orgPaymentRows, setOrgPaymentRows] = useState<OperationalPaymentRow[]>([]);
  const [orgModules, setOrgModules] = useState<OrgModuleItem[] | null>(null);
  const [sectionErrors, setSectionErrors] = useState<DashboardSectionErrors>(EMPTY_SECTION_ERRORS);

  const reload = useCallback(async () => {
    setLoading(true);
    setSectionErrors(EMPTY_SECTION_ERRORS);
    const period = last30dRange();

    try {
      if (branchScopeId != null) {
        const branches = await fetchTenantBranches(apiClient);
        setBranchName(branches.find((b) => b.id === branchScopeId)?.name ?? null);
      } else {
        setBranchName(null);
      }
    } catch {
      setBranchName(null);
    }

    const settled = await Promise.allSettled([
      canTariffs ? fetchOrgPlanStatus(apiClient) : Promise.resolve(null),
      canClients ? fetchOrgClientsList(apiClient, { limit: 5, offset: 0 }) : Promise.resolve({ items: [], total: 0 }),
      canTrainers ? fetchAdminTrainersList(apiClient, { limit: 1, offset: 0 }) : Promise.resolve({ items: [], total: 0 }),
      canYc ? fetchYcAdminSyncStatus(apiClient) : Promise.resolve(null),
      canAuditPay ? fetchPaymentGatewayAuditTotalsAdmin(apiClient, period) : Promise.resolve(null),
      canAuditPay ? fetchPaymentGatewayAuditStats(apiClient) : Promise.resolve(null),
      canAuditPay
        ? fetchPaymentGatewayAuditListAdmin(apiClient, {
            limit: 60,
            offset: 0,
            created_from: period.created_from,
            created_to: period.created_to
          })
        : Promise.resolve({ items: [], total: 0 }),
      canAuditPay
        ? fetchOrgPaymentsStats(apiClient)
        : Promise.resolve(null),
      canAuditPay
        ? fetchOperationalPaymentsList(apiClient, { limit: 5, offset: 0 })
        : Promise.resolve({ items: [], total: 0 }),
      getOrganizationMeModules(apiClient)
    ]);

    const [planR, clientsR, trainersR, ycR, totalsR, statsR, listR, payStatsR, payListR, modulesR] = settled;
    const nextErrors: DashboardSectionErrors = { ...EMPTY_SECTION_ERRORS };

    if (canTariffs) {
      if (planR.status === 'fulfilled') {
        setPlanStatus(planR.value);
      } else {
        nextErrors.plan = userFacingLoadError(planR.reason);
      }
    } else {
      setPlanStatus(null);
    }

    if (canClients) {
      if (clientsR.status === 'fulfilled') {
        setClientsTotal(clientsR.value.total);
        setClientsRows(clientsR.value.items);
      } else {
        nextErrors.clients = userFacingLoadError(clientsR.reason);
      }
    } else {
      setClientsTotal(null);
      setClientsRows([]);
    }

    if (canTrainers) {
      if (trainersR.status === 'fulfilled') {
        setTrainersTotal(trainersR.value.total);
      } else {
        nextErrors.trainers = userFacingLoadError(trainersR.reason);
      }
    } else {
      setTrainersTotal(null);
    }

    if (canYc) {
      if (ycR.status === 'fulfilled') {
        setYc(ycR.value);
      } else {
        nextErrors.yc = userFacingLoadError(ycR.reason);
      }
    } else {
      setYc(null);
    }

    if (canAuditPay) {
      if (totalsR.status === 'fulfilled') {
        setPayTotals(totalsR.value);
      } else {
        nextErrors.auditTotals = userFacingLoadError(totalsR.reason);
      }
    } else {
      setPayTotals(null);
    }

    if (statsR.status === 'fulfilled') {
      setPayStats(statsR.value);
    } else {
      setPayStats(null);
    }

    if (canAuditPay) {
      if (payStatsR.status === 'fulfilled') {
        setOrgPayStats(payStatsR.value);
      } else {
        nextErrors.payments = userFacingLoadError(payStatsR.reason);
      }
      if (payListR.status === 'fulfilled') {
        setOrgPaymentRows(payListR.value.items);
      } else {
        nextErrors.paymentRows = userFacingLoadError(payListR.reason);
      }
    } else {
      setOrgPayStats(null);
      setOrgPaymentRows([]);
    }

    if (listR.status === 'fulfilled') {
      const rows = filterByBranch(listR.value.items, branchScopeId).slice(0, 5);
      setPaymentRows(rows);
    } else {
      setPaymentRows([]);
    }

    if (modulesR.status === 'fulfilled' && Array.isArray(modulesR.value?.items)) {
      setOrgModules(modulesR.value.items);
    } else if (modulesR.status === 'rejected') {
      nextErrors.modules = userFacingLoadError(modulesR.reason);
    } else {
      nextErrors.modules = userFacingLoadError(new Error(''));
    }

    setSectionErrors(nextErrors);
    setHasLoaded(true);
    setLoading(false);
  }, [apiClient, branchScopeId, canAuditPay, canClients, canTariffs, canTrainers, canYc]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const subtitle = useMemo(() => {
    if (branchScopeId != null) {
      return `Филиал: ${branchName ?? `#${branchScopeId}`}. Журнал шлюза с branch_id отфильтрован; суммы оплат за период — по организации.`;
    }
    const parts: string[] = ['клиентам'];
    if (canTrainers) parts.push('специалистам');
    if (canAuditPay) parts.push('оплатам за 30 дней');
    if (canYc) parts.push('интеграции YClients');
    return `Сводка по ${parts.join(', ')}. Подробности — в разделах меню.`;
  }, [branchName, branchScopeId, canTrainers, canAuditPay, canYc]);

  const ycTitle = useMemo(() => {
    if (!yc) return '—';
    if (!yc.sync_enabled) return 'выкл';
    const ev = yc.total_events_24h ?? 0;
    const er = yc.errors_24h ?? 0;
    const wr = yc.warnings_24h ?? 0;
    return `${ev} событ. / 24ч · ошибок ${er} · предупр. ${wr}`;
  }, [yc]);

  const importHint = useMemo(() => {
    const s = yc?.import_stats_all_time;
    if (!s) return null;
    return `Импорт (всего): события ${s.events ?? 0} · записи ${s.records ?? 0} · пользователи ${s.users ?? 0}`;
  }, [yc]);

  const planLines = useMemo(() => {
    if (!planStatus) return null;
    const parts: string[] = [];
    const life = buildLifecycleSummaryLine(planStatus);
    if (life) parts.push(life);
    parts.push(buildLimitsSummaryText(planStatus));
    const norm = buildNormalSubscriptionStatusLine(planStatus);
    if (norm) parts.push(norm);
    return parts;
  }, [planStatus]);

  const orgModulesList = useMemo(() => orgModules ?? [], [orgModules]);
  const connectedModules = useMemo(
    () => orgModulesList.filter((m) => m.enabled),
    [orgModulesList]
  );
  const disconnectedModules = useMemo(
    () => orgModulesList.filter((m) => !m.enabled),
    [orgModulesList]
  );
  const initialLoading = loading && !hasLoaded;
  const clientsPending = initialLoading || (loading && clientsTotal == null);
  const trainersPending = initialLoading || (loading && trainersTotal == null);
  const auditPayPending = initialLoading || (loading && payTotals == null);
  const ycPending = initialLoading || (loading && yc == null);
  const planPending = initialLoading || (loading && planStatus == null);
  const modulesPending = initialLoading || (loading && orgModules == null);
  const paymentsPending = initialLoading || (loading && orgPayStats == null);
  const retry = useCallback(() => {
    void reload();
  }, [reload]);

  return (
    <Stack gap={4} data-testid="tenant-org-ops-dashboard">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <h1 className="staff-m-0 text-xl font-semibold tracking-tight text-[hsl(var(--ui-foreground))]">Дашборд организации</h1>
          <p className="staff-m-0 max-w-3xl text-sm leading-snug text-[hsl(var(--ui-muted-foreground))]">{subtitle}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="inline-flex shrink-0 items-center gap-1.5"
          onClick={retry}
          disabled={loading}
          aria-busy={loading}
          data-testid="dashboard-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          {loading ? 'Обновление…' : 'Обновить'}
        </Button>
      </header>

      {organizationId != null ? <DashboardTodayCard organizationId={organizationId} branchId={branchScopeId} /> : null}


      <div
        className={`grid gap-3 ${canAuditPay ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'}`}
      >
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <div className="rounded-md bg-[hsl(var(--ui-primary)/0.12)] p-2 text-[hsl(var(--ui-primary))]">
              <Users className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">Клиенты</p>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]">
                {clientsPending ? '…' : !canClients ? '—' : String(clientsTotal ?? '—')}
              </p>
              {sectionErrors.clients && clientsTotal == null ? (
                <p className="staff-m-0 text-xs text-[hsl(var(--ui-destructive))]">Не удалось загрузить</p>
              ) : (
                <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">в базе организации</p>
              )}
              {canClients ? (
                <Link className="text-xs font-medium text-[hsl(var(--ui-primary))] underline-offset-2 hover:underline" to={ROUTE_PATHS.clients}>
                  Каталог клиентов
                </Link>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <div className="rounded-md bg-[hsl(var(--ui-primary)/0.12)] p-2 text-[hsl(var(--ui-primary))]">
              <UserRound className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">
                Специалисты{branchScopeId != null ? ' · филиал' : ''}
              </p>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]">
                {trainersPending ? '…' : !canTrainers ? '—' : String(trainersTotal ?? '—')}
              </p>
              {sectionErrors.trainers && trainersTotal == null ? (
                <p className="staff-m-0 text-xs text-[hsl(var(--ui-destructive))]">Не удалось загрузить</p>
              ) : null}
              {canTrainers ? (
                <Link className="text-xs font-medium text-[hsl(var(--ui-primary))] underline-offset-2 hover:underline" to={ROUTE_PATHS.trainers}>
                  Каталог специалистов
                </Link>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {canAuditPay ? (
          <Card className="sm:col-span-2 lg:col-span-1">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="rounded-md bg-[hsl(var(--ui-primary)/0.12)] p-2 text-[hsl(var(--ui-primary))]">
                <Wallet className="h-4 w-4" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">Оплаты (30 дн.)</p>
                {auditPayPending ? (
                  <p className="text-lg font-semibold">…</p>
                ) : payTotals ? (
                  <div className="space-y-1 text-sm leading-tight">
                    <div className="flex justify-between gap-2 tabular-nums">
                      <span className="text-[hsl(var(--ui-muted-foreground))]">Касса</span>
                      <span className="font-medium">{formatRubMoney(payTotals.cash_total)}</span>
                    </div>
                    <div className="flex justify-between gap-2 tabular-nums">
                      <span className="text-[hsl(var(--ui-muted-foreground))]">Банк</span>
                      <span className="font-medium">{formatRubMoney(payTotals.bank_total)}</span>
                    </div>
                    {sectionErrors.auditTotals ? (
                      <p className="staff-m-0 text-xs text-[hsl(var(--ui-destructive))]">Не удалось обновить</p>
                    ) : null}
                  </div>
                ) : sectionErrors.auditTotals ? (
                  <div className="space-y-1">
                    <p className="staff-m-0 text-sm text-[hsl(var(--ui-destructive))]">Не удалось загрузить</p>
                    <RetryButton onRetry={retry} disabled={loading} className="inline-flex items-center gap-1.5" />
                  </div>
                ) : (
                  <p className="text-lg font-semibold">—</p>
                )}
                {branchScopeId != null ? (
                  <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
                    Суммы по аудиту за период по организации (без разбивки по филиалу в API).
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {canYc ? (
        <Card>
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 shrink-0 text-[hsl(var(--ui-primary))]" aria-hidden />
                <span className="text-sm font-medium text-[hsl(var(--ui-foreground))]">YClients</span>
                {ycPending ? (
                  <Badge variant="neutral">…</Badge>
                ) : yc?.sync_enabled ? (
                  <Badge variant="neutral" className="font-normal">
                    Синхронизация включена
                  </Badge>
                ) : yc ? (
                  <Badge variant="neutral" className="border-amber-500/40 text-amber-900 dark:text-amber-200">
                    Выкл
                  </Badge>
                ) : null}
              </div>
              <span className="hidden h-4 w-px bg-[hsl(var(--ui-border))] sm:inline" aria-hidden />
              <div className="min-w-0 space-y-1 text-xs leading-snug text-[hsl(var(--ui-muted-foreground))] sm:max-w-[32rem]">
                <p className="staff-m-0">
                  {sectionErrors.yc ? (
                    <span className="text-[hsl(var(--ui-destructive))]">
                      {sectionErrors.yc}
                      {yc ? ' Показаны предыдущие данные.' : ''}
                    </span>
                  ) : ycPending ? (
                    '…'
                  ) : (
                    ycTitle
                  )}
                </p>
                {sectionErrors.yc ? (
                  <RetryButton onRetry={retry} disabled={loading} className="inline-flex items-center gap-1.5" />
                ) : null}
              </div>
            </div>
            <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))] sm:max-w-xs sm:text-right">
              {ycPending ? '…' : sectionErrors.yc && !yc ? '—' : importHint ?? 'Нет данных импорта.'}
            </p>
            <Link
              to={ROUTE_PATHS.ycSyncSummary}
              className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex w-full shrink-0 sm:w-auto' })}
            >
              Сводка YClients
            </Link>
          </CardContent>
        </Card>
      ) : null}

      {canAuditPay ? (
        <Card data-testid="dashboard-payments-card">
          <CardHeader className="space-y-0.5 pb-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-base">Платежи</CardTitle>
                <CardDescription>Выручка по успешным платежам за 30 дней.</CardDescription>
              </div>
              <Link
                to={ROUTE_PATHS.payments}
                className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex' })}
              >
                Все платежи
              </Link>
            </div>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4 p-0 pb-3">
            {paymentsPending ? (
              <div className="px-4 py-6">
                <Spinner label="Загрузка…" />
              </div>
            ) : sectionErrors.payments && !orgPayStats ? (
              <div className="px-3 py-4 sm:px-4" data-testid="dashboard-payments-error">
                <EmptyState title="Не удалось загрузить платежи" description={sectionErrors.payments}>
                  <RetryButton
                    onRetry={retry}
                    disabled={loading}
                    className="mt-3 inline-flex items-center gap-1.5"
                  />
                </EmptyState>
              </div>
            ) : orgPayStats ? (
              <div className="space-y-3 px-3 py-4 sm:px-4">
                {sectionErrors.payments ? (
                  <div data-testid="dashboard-payments-refresh-error">
                    <RefreshWarning error={sectionErrors.payments} onRetry={retry} />
                  </div>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                    <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">Выручка</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">{formatRubMoney(orgPayStats.total_revenue)}</p>
                  </div>
                  <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                    <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">Платежей</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">{orgPayStats.total_count.toLocaleString('ru-RU')}</p>
                  </div>
                  <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                    <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">Текущий месяц</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">{formatRubMoney(orgPayStats.month_current_total)}</p>
                    {orgPayStats.month_delta_pct != null && (
                      <p className={`text-xs ${orgPayStats.month_delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {orgPayStats.month_delta_pct >= 0 ? '+' : ''}{orgPayStats.month_delta_pct.toFixed(1)}%
                      </p>
                    )}
                  </div>
                </div>

                {orgPayStats.total_count === 0 ? (
                  <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">
                    За последние 30 дней успешных платежей не было.
                  </p>
                ) : null}

                {orgPayStats.by_provider.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">По провайдерам</p>
                    <div className="flex flex-wrap gap-2">
                      {orgPayStats.by_provider.map((p) => (
                        <span key={p.provider} className="inline-flex items-center gap-1 rounded-sm border border-[hsl(var(--ui-border))] px-2 py-1 text-xs">
                          <span>{paymentProviderLabel(p.provider)}</span>
                          <span className="font-medium tabular-nums">{formatRubMoney(p.revenue)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {sectionErrors.paymentRows ? (
                  <div data-testid="dashboard-payment-rows-refresh-error">
                    <RefreshWarning error={sectionErrors.paymentRows} onRetry={retry} />
                  </div>
                ) : null}

                {orgPaymentRows.length > 0 && (
                  <div className="border-t border-[hsl(var(--ui-border))] pt-3" data-testid="dashboard-payments-recent">
                    <p className="mb-1 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">Последние</p>
                    <TableScroll>
                      <Table className="min-w-[24rem]">
                        <THead>
                          <Tr>
                            <Th className="w-[8rem]">Дата</Th>
                            <Th className="text-right">Сумма</Th>
                            <Th>Статус</Th>
                            <Th>Провайдер</Th>
                          </Tr>
                        </THead>
                        <TBody>
                          {orgPaymentRows.map((row) => (
                            <Tr key={row.id}>
                              <Td className="whitespace-nowrap text-xs text-[hsl(var(--ui-muted-foreground))]">
                                {row.created_at ? formatOrgDate(row.created_at, tz) : ''}
                              </Td>
                              <Td className="text-right text-xs font-medium tabular-nums">
                                {formatPaymentAmount(row.amount_cents, row.currency)}
                              </Td>
                              <Td className="text-xs">{paymentStatusLabel(row.status)}</Td>
                              <Td className="text-xs">{paymentProviderLabel(row.provider)}</Td>
                            </Tr>
                          ))}
                        </TBody>
                      </Table>
                    </TableScroll>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-4 py-4" data-testid="dashboard-payments-zero">
                <EmptyState
                  title="Платежей за последние 30 дней нет"
                  description="Успешные платежи появятся здесь после первой оплаты."
                />
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {canClients ? (
        <Card data-testid="dashboard-clients-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Новые клиенты</CardTitle>
            <CardDescription>Пять последних в выборке — полный список в разделе «Клиенты».</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {clientsPending ? (
              <div className="px-4 py-6">
                <Spinner label="Загрузка…" />
              </div>
            ) : sectionErrors.clients && clientsTotal == null ? (
              <div className="px-3 py-4 sm:px-4" data-testid="dashboard-clients-error">
                <EmptyState title="Не удалось загрузить клиентов" description={sectionErrors.clients}>
                  <RetryButton
                    onRetry={retry}
                    disabled={loading}
                    className="mt-3 inline-flex items-center gap-1.5"
                  />
                </EmptyState>
              </div>
            ) : (
              <>
                {sectionErrors.clients ? (
                  <div className="px-3 pt-3 sm:px-4" data-testid="dashboard-clients-refresh-error">
                    <RefreshWarning error={sectionErrors.clients} onRetry={retry} />
                  </div>
                ) : null}
                {clientsRows.length === 0 ? (
                  <div className="px-3 py-4 sm:px-4" data-testid="dashboard-clients-empty">
                    <EmptyState
                      title={clientsTotal === 0 ? 'Клиентов пока нет' : 'Клиенты не найдены'}
                      description={
                        clientsTotal === 0
                          ? 'Добавьте первого клиента или пригласите его в организацию.'
                          : 'В выборке нет строк — обновите данные или откройте каталог.'
                      }
                    >
                      <Link
                        to={ROUTE_PATHS.clients}
                        className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'mt-3 inline-flex' })}
                      >
                        Перейти в каталог
                      </Link>
                    </EmptyState>
                  </div>
                ) : (
                  <TableScroll>
                    <Table className="min-w-[24rem]">
                      <THead>
                        <Tr>
                          <Th>Клиент</Th>
                          <Th>Email</Th>
                          <Th>Роль</Th>
                        </Tr>
                      </THead>
                      <TBody>
                        {clientsRows.map((row) => (
                          <Tr key={row.id}>
                            <Td className="font-medium">{row.full_name?.trim() || row.email}</Td>
                            <Td className="text-sm text-[hsl(var(--ui-muted-foreground))]">{row.email}</Td>
                            <Td className="text-sm">{staffRoleLabel(row.role)}</Td>
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  </TableScroll>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {canTariffs ? (
        <Card data-testid="dashboard-plan-card">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Тариф и лимиты</CardTitle>
              <CardDescription>Тариф, лимиты и отсрочки по данным учёта.</CardDescription>
            </div>
            <Link
              to={ROUTE_PATHS.tariffs}
              className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex w-full shrink-0 sm:w-auto' })}
            >
              Мой тариф
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {planPending ? (
              <Spinner label="Загрузка…" />
            ) : sectionErrors.plan && !planStatus ? (
              <div data-testid="dashboard-plan-error">
                <EmptyState title="Не удалось загрузить тариф" description={sectionErrors.plan}>
                  <RetryButton
                    onRetry={retry}
                    disabled={loading}
                    className="mt-3 inline-flex items-center gap-1.5"
                  />
                </EmptyState>
              </div>
            ) : (
              <>
                {sectionErrors.plan ? (
                  <div data-testid="dashboard-plan-refresh-error">
                    <RefreshWarning error={sectionErrors.plan} onRetry={retry} />
                  </div>
                ) : null}
                {planLines ? (
                  <div className="space-y-1 text-sm leading-relaxed text-[hsl(var(--ui-foreground))]">
                    {planLines.map((line, idx) => (
                      <p key={`plan-line-${idx}`} className="staff-m-0">
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">Нет данных плана.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card data-testid="tenant-org-modules-card">
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Blocks className="h-4 w-4 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
              <CardTitle className="text-base">Модули тарифа</CardTitle>
            </div>
            <CardDescription>
              Подключенные и не подключенные модули в текущем тарифе организации.
            </CardDescription>
          </div>
          {canTariffs ? (
            <Link
              to={ROUTE_PATHS.tariffs}
              className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex w-full shrink-0 sm:w-auto' })}
            >
              Управление тарифом
            </Link>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {modulesPending ? (
            <Spinner label="Загрузка модулей…" />
          ) : sectionErrors.modules && orgModules == null ? (
            <div data-testid="dashboard-modules-error">
              <EmptyState title="Не удалось загрузить модули" description={sectionErrors.modules}>
                <RetryButton
                  onRetry={retry}
                  disabled={loading}
                  className="mt-3 inline-flex items-center gap-1.5"
                />
              </EmptyState>
            </div>
          ) : (
            <>
              {sectionErrors.modules ? (
                <div data-testid="dashboard-modules-refresh-error">
                  <RefreshWarning error={sectionErrors.modules} onRetry={retry} />
                </div>
              ) : null}
              {orgModulesList.length === 0 ? (
                <EmptyState
                  title="Модули не найдены"
                  description="В текущем тарифе нет доступных модулей."
                  dataTestId="dashboard-modules-empty"
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Подключенные модули */}
                  <div className="space-y-2 rounded-md border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted)/0.15)] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ui-muted-foreground))]">
                        Подключено ({connectedModules.length})
                      </span>
                      <Badge variant="primary" className="text-xs">Активны</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {connectedModules.length === 0 ? (
                        <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">Нет активных модулей</p>
                      ) : (
                        connectedModules.map((m) => (
                          <div
                            key={m.module_code}
                            data-testid={`dashboard-module-connected-${m.module_code}`}
                            className="inline-flex items-center gap-1.5 rounded border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-2.5 py-1 text-xs text-[hsl(var(--ui-foreground))]"
                            title={m.is_core ? 'Базовый модуль платформы' : 'Входит в тариф и включен'}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                            <span className="font-medium">{moduleCodeLabelRu(m.module_code)}</span>
                            {m.is_core ? (
                              <span className="text-[10px] text-[hsl(var(--ui-muted-foreground))]">(ядро)</span>
                            ) : null}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Не подключенные модули */}
                  <div className="space-y-2 rounded-md border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted)/0.15)] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ui-muted-foreground))]">
                        Не подключено ({disconnectedModules.length})
                      </span>
                      <Badge variant="neutral" className="text-xs">Неактивны</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {disconnectedModules.length === 0 ? (
                        <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">Все доступные модули подключены</p>
                      ) : (
                        disconnectedModules.map((m) => {
                          const locked = !m.in_plan;
                          return (
                            <div
                              key={m.module_code}
                              data-testid={`dashboard-module-disconnected-${m.module_code}`}
                              className="inline-flex items-center gap-1.5 rounded border border-dashed border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-2.5 py-1 text-xs text-[hsl(var(--ui-muted-foreground))]"
                              title={locked ? 'Не входит в текущий тарифный план' : 'Доступен в вашем тарифе, но отключен в настройках'}
                            >
                              {locked ? (
                                <Lock className="h-3.5 w-3.5 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
                              ) : (
                                <XCircle className="h-3.5 w-3.5 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
                              )}
                              <span>{moduleCodeLabelRu(m.module_code)}</span>
                              <span className="text-[10px] opacity-75">
                                {locked ? '(не в тарифе)' : '(отключен)'}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
